import React, { useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import {
  HeartFilled,
  MessageOutlined,
  EditOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getImagePath } from '../../../utils/format/valueFormat';
import { TProject, TProjectLike } from '../../../models/project/project';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import CommentsDrawer from './CommentsDrawer';
import { CODIFICATION_TYPE_USERS } from '../../../constant/codification';
import { useLike } from '../../../hooks/project_likes';

interface ProjectCardProps {
  studentProject: TProject;
  likedStatus: TProjectLike[];
  onEditProject?: (project: TProject) => void;
  showTags?: boolean;
}

// Clés exactes des étiquettes non affichées sur la carte (tc_tilmicode_be,
// constant/tags.go). L'ancien filtre par sous-chaîne « age » masquait aussi
// toute clé contenant ces lettres (IMAGE, LANGUAGE, STAGE…).
const HIDDEN_TAG_KEYS: ReadonlySet<string> = new Set(['BIZ', 'AGE']);

const ProjectCard: React.FC<ProjectCardProps> = ({
  studentProject,
  likedStatus = [],
  onEditProject,
  showTags = false,
}) => {
  const navigate = useNavigate();
  const { getUserAppId, getCodeTypeUser } = useTilmiAuth();

  const palette = useMemo(
    () => ({
      primary: '#2D9CC9',
      secondary: '#8BBE1E',
      border: 'rgba(45,156,201,0.18)',
      muted: 'rgba(12, 24, 42, 0.7)',
    }),
    []
  );

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [commentCount, setCommentCount] = useState<number>(
    studentProject.nb_comments ?? 0
  );
  const handleAddComment = () => setCommentCount((c) => c + 1);

  const { liked, count: likesCount, toggle: handleToggleLike } = useLike(
    studentProject.id,
    likedStatus.some(
      (l) => l.project_id === studentProject.id && l.userapp_id === getUserAppId()
    ),
    studentProject.nb_likes ?? 0
  );

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/project-details/${studentProject.id}`);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditProject?.(studentProject);
  };

  const handleCommentsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDrawerVisible(true);
  };

  const renderTagList = () => {
    if (!studentProject.tags) return null;

    const entries = Object.entries(studentProject.tags).filter(
      ([, raw]) => (raw as any)?.visible
    );

    if (!entries.length) return null;

    return (
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map(([key, raw]) => {
          if (HIDDEN_TAG_KEYS.has(key)) {
            return null;
          }
          const tag = raw as { value: string; visible: boolean; icon?: string };
          const label = key.replace(/_/g, ' ');
          const value = tag.value;
          return (
            <div
              key={key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                borderRadius: 16,
                background: '#f7fbff',
                border: `1px solid ${palette.border}`,
                fontWeight: 600,
                color: palette.muted,
                fontSize: 13,
              }}
            >
              <span style={{ textTransform: 'capitalize' }}>{label}</span>
              <span>{value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const typeLabel = studentProject.project_type?.label;
  const createdAt = studentProject.created_at
    ? new Date(studentProject.created_at).toLocaleDateString()
    : null;

  return (
    <>
      <article
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          borderRadius: 28,
          padding: 18,
          background: 'linear-gradient(145deg, rgba(255,255,255,0.95), rgba(245,249,255,0.92))',
          border: `1px solid ${palette.border}`,
          boxShadow: '0 22px 48px rgba(7,24,38,0.12)',
          height: '100%',
        }}
      >
        <div
          style={{
            position: 'relative',
            borderRadius: 20,
            overflow: 'hidden',
            aspectRatio: '3 / 2',
          }}
        >
          <img
            alt={studentProject.name}
            src={
              studentProject.image
                ? getImagePath(studentProject.image.file_path)
                : '/fallback.png'
            }
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(7,24,38,0.05) 0%, rgba(7,24,38,0.35) 100%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 14,
              left: 14,
              right: 14,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Tooltip title={studentProject.name}>
              <span
                style={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 18,
                  textShadow: '0 6px 16px rgba(0,0,0,0.35)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '70%',
                }}
              >
                {studentProject.name}
              </span>
            </Tooltip>
            <button
              type="button"
              onClick={handlePlayClick}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 999,
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                color: '#fff',
                boxShadow: '0 12px 24px rgba(45,156,201,0.35)',
              }}
            >
              <PlayCircleOutlined />
              Play
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {typeLabel && (
              <span
                style={{
                  borderRadius: 12,
                  padding: '4px 12px',
                  background: `${palette.primary}12`,
                  fontWeight: 600,
                  color: palette.muted,
                  border: `1px solid ${palette.border}`,
                }}
              >
                {typeLabel}
              </span>
            )}
            {createdAt && (
              <span
                style={{
                  borderRadius: 12,
                  padding: '4px 12px',
                  background: `${palette.secondary}12`,
                  fontWeight: 600,
                  color: palette.muted,
                  border: `1px solid ${palette.border}`,
                }}
              >
                {createdAt}
              </span>
            )}
          </div>
          <Tooltip title={studentProject.description}>
            <p
              style={{
                margin: 0,
                color: palette.muted,
                fontSize: 15,
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {studentProject.description}
            </p>
          </Tooltip>
        </div>

        {renderTagList()}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 'auto',
            borderTop: `1px solid ${palette.border}`,
            paddingTop: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 18,
            }}
          >
            <button
              type="button"
              onClick={handleToggleLike}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                border: 'none',
                background: 'transparent',
                color: liked ? palette.primary : palette.muted,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              <HeartFilled style={{ fontSize: 18 }} />
              {likesCount}
            </button>

            <button
              type="button"
              onClick={handleCommentsClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                border: 'none',
                background: 'transparent',
                color: palette.muted,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              <MessageOutlined style={{ fontSize: 18 }} />
              {commentCount}
            </button>
          </div>

          {onEditProject && getCodeTypeUser() === CODIFICATION_TYPE_USERS.MINOR && (
            <button
              type="button"
              onClick={handleEditClick}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                border: `1px solid ${palette.border}`,
                borderRadius: 999,
                padding: '6px 14px',
                background: 'transparent',
                color: palette.muted,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              <EditOutlined />
              Modifier
            </button>
          )}
        </div>
      </article>

      <CommentsDrawer
        projectId={studentProject.id}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        handleAddComment={handleAddComment}
      />
    </>
  );
};

export default ProjectCard;
